'use client';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowRight } from '@mui/icons-material';
import {
  Box,
  Collapse,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';

import { Pagination, StyledTable, StyledTableContainer } from '~/shared';
import {
  BuscaPatrocinadaProductsDTO,
  TabFiltersProps,
} from '~/types/keywordPerformance';

import { useGetBuscaPatrocinadaProducts } from '../../hooks/useGetBuscaPatrocinadaProducts';
import { useMetricSort } from '../../hooks/useMetricSort';

import { CellValue } from './CellValue';
import { LoadingRow } from './LoadingRow';
import { MetricTableHead } from './MetricTableHead';
import { ProductApiExpandedRow } from './ProductApiExpandedRow';

const PAGE_SIZE = 15;

const METRIC_TABLE_COL_SPAN = 6;

export function ProductsTab({
  debouncedQuery,
  startDate,
  endDate,
}: TabFiltersProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { getSortOrder, toggleSort, orderParams } = useMetricSort();

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, startDate, endDate, orderParams]);

  const params = useMemo<BuscaPatrocinadaProductsDTO>(
    () => ({
      keyword: debouncedQuery || undefined,
      startDate:
        startDate && isValid(startDate)
          ? format(startDate, 'yyyy-MM-dd')
          : undefined,
      FinishDate:
        endDate && isValid(endDate) ? format(endDate, 'yyyy-MM-dd') : undefined,
      ...orderParams,
      Page: page,
      Count: PAGE_SIZE,
    }),
    [debouncedQuery, startDate, endDate, orderParams, page]
  );

  const { data, isFetching } = useGetBuscaPatrocinadaProducts(params);
  const list = data?.list ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = useCallback((key: string) => {
    setExpandedId(prev => (prev === key ? null : key));
  }, []);

  return (
    <>
      <StyledTableContainer>
        <StyledTable aria-label="Tabela de desempenho de produtos">
          <MetricTableHead
            firstColumnLabel="Nome do Produto"
            getSortOrder={getSortOrder}
            toggleSort={toggleSort}
          />

          <TableBody>
            {isFetching && <LoadingRow colSpan={METRIC_TABLE_COL_SPAN} />}

            {!isFetching && list.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={METRIC_TABLE_COL_SPAN}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">Sem resultados</Typography>
                </TableCell>
              </TableRow>
            )}

            {!isFetching &&
              list.map((item, index) => {
                const key = `${item.productName}-${item.keyword}-${index}`;
                const isExpanded = expandedId === key;
                return (
                  <Fragment key={key}>
                    <TableRow
                      hover
                      selected={isExpanded}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(key)}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <KeyboardArrowRight
                            sx={{
                              fontSize: 18,
                              flexShrink: 0,
                              color: 'text.secondary',
                              transition: 'transform 0.2s ease',
                              transform: isExpanded
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.productName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <CellValue value={item.impressions} />
                      </TableCell>
                      <TableCell align="center">
                        <CellValue value={item.clicks} />
                      </TableCell>
                      <TableCell align="center">
                        <CellValue value={item.ctr} />
                      </TableCell>
                      <TableCell align="center">
                        <CellValue value={item.saleValue} />
                      </TableCell>
                      <TableCell align="center">
                        <CellValue value={item.gmv} />
                      </TableCell>
                    </TableRow>

                    <TableRow
                      sx={{
                        '& > td': {
                          p: 0,
                          borderBottom: isExpanded ? undefined : 'none',
                        },
                      }}
                    >
                      <TableCell colSpan={METRIC_TABLE_COL_SPAN}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <ProductApiExpandedRow item={item} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      <Pagination
        disabled={totalPages === 1 || isFetching || total === 0}
        page={page}
        count={totalPages}
        nextPage={() => setPage(p => Math.min(p + 1, totalPages))}
        prevPage={() => setPage(p => Math.max(p - 1, 1))}
        onChange={(_, newPage) => setPage(newPage)}
      />
    </>
  );
}
